// Need to import next/link in order to create a layer on top of it
// eslint-disable-next-line no-restricted-imports
import Link from 'next/link';
import { forwardRef, ReactNode, useMemo } from 'react';

import { useMarket } from '../../providers/MarketProvider';
import { getLocalizedUrl } from '../../utils/markets';
import { ExternalLink } from '../ExternalLink/ExternalLink';

type LocalizedLinkProps = React.ComponentProps<typeof Link> & {
  children: ReactNode;
  prefetch?: false;
};

const isExternalUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch (_) {
    // noop
  }

  return url.includes('/travel/plan');
};

export const LocalizedLink = forwardRef<HTMLAnchorElement, LocalizedLinkProps>(
  (props, ref) => {
    const market = useMarket();
    const { href, prefetch, ...rest } = props;
    const isExternal = isExternalUrl(href.toString());
    // Market can be undefined if the component is rendered on the internal market selector page
    const localizedHref = useMemo(
      () =>
        market?.host && !isExternal
          ? getLocalizedUrl(market, typeof href === 'string' ? href : '')
          : null,
      [market, isExternal, href]
    );

    if (isExternal) {
      return <ExternalLink href={href.toString()} {...rest} />;
    }

    const shouldNotPrefetch = prefetch === false && !localizedHref;

    return (
      <Link
        href={localizedHref ?? href}
        ref={ref}
        // pre-fetch urls that we can localize, that way we know they are within the nextjs app
        prefetch={shouldNotPrefetch ? false : undefined}
        // https://nextjs.org/docs/messages/react-hydration-error#solution-3-using-suppresshydrationwarning
        suppressHydrationWarning
        {...rest}
      />
    );
  }
);

LocalizedLink.displayName = 'LocalizedLink';
